import { useState, useEffect, useRef } from 'react';
import { Button, Input, Spin, notification } from 'antd';
import {
  EnvironmentOutlined, EyeOutlined, MessageOutlined, DeleteOutlined,
  SendOutlined, CameraOutlined, BankOutlined, GlobalOutlined,
  BulbOutlined, ToolOutlined, BellFilled,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G } from '../shared/constants';
import { authHeader } from '../shared/utils';

// ── PostCard helpers ──────────────────────────────────────────────────────────
export const CAT_META = {
  news:    { label: 'समाचार', icon: <GlobalOutlined />,  color: '#1d4ed8', bg: '#eff6ff' },
  vacancy: { label: 'रोजगार', icon: <BankOutlined />,    color: '#92400e', bg: '#fef3c7' },
  thought: { label: 'विचार',  icon: <BulbOutlined />,    color: '#6d28d9', bg: '#ede9fe' },
  service: { label: 'सेवा',   icon: <ToolOutlined />,    color: '#065f46', bg: '#ecfdf5' },
  notice:  { label: 'घोषणा', icon: <BellFilled />,      color: '#991b1b', bg: '#fef2f2' },
};
export const CATS = Object.entries(CAT_META).map(([k, v]) => ({ key: k, ...v }));

export function timeAgo(d) {
  if (!d) return '';
  const diff = (Date.now() - new Date(d)) / 1000;
  if (diff < 60)    return 'अभी';
  if (diff < 3600)  return `${Math.floor(diff / 60)} मिनट पहले`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} घंटे पहले`;
  return `${Math.floor(diff / 86400)} दिन पहले`;
}

export function lastSeen(ts) {
  if (!ts) return null;
  const min = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (min < 5)   return { text: 'अभी ऑनलाइन', dotColor: '#22c55e', textColor: '#15803d', online: true };
  if (min < 60)  return { text: `${min} मि॰ पहले`,   dotColor: '#d1d5db', textColor: '#9ca3af', online: false };
  const hr = Math.floor(min / 60);
  if (hr < 24)   return { text: `${hr} घं॰ पहले`,    dotColor: '#d1d5db', textColor: '#9ca3af', online: false };
  const day = Math.floor(hr / 24);
  if (day === 1) return { text: 'कल',               dotColor: '#e5e7eb', textColor: '#d1d5db', online: false };
  if (day < 7)   return { text: `${day} दिन पहले`,  dotColor: '#e5e7eb', textColor: '#d1d5db', online: false };
  return null;
}

export function LastSeenBadge({ ts, style = {} }) {
  const s = lastSeen(ts);
  if (!s) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, ...style }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
        background: s.dotColor,
        boxShadow: s.online ? '0 0 0 2px #dcfce7' : 'none',
      }} />
      <span style={{ fontSize: 10, color: s.textColor, fontWeight: s.online ? 700 : 400 }}>
        {s.text}
      </span>
    </span>
  );
}

export function PostCard({ post, onOpen, onReact, isCommentExpanded, onToggleComment, myUserId, onDelete }) {
  const cat = CAT_META[post.category] || CAT_META.thought;
  const ini = n => (n || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const [imgErr, setImgErr] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isOwn = myUserId && post.user_id === myUserId;

  const handleDelete = async () => {
    if (!window.confirm('क्या आप यह पोस्ट हटाना चाहते हैं?')) return;
    setDeleting(true);
    try {
      await axios.delete(`${baseUrl}/member/posts/${post.id}`, { headers: authHeader() });
      onDelete?.(post.id);
    } catch { notification.error({ message: 'पोस्ट हटाने में त्रुटि' }); }
    finally { setDeleting(false); setShowMenu(false); }
  };
  const CONTENT_LIMIT = 220;
  const shouldClamp = post.content?.length > CONTENT_LIMIT;

  const REACTIONS = [
    { key: 'like',      icon: '👍', label: 'पसंद',   activeColor: '#9a3412', activeBg: '#fef3c7' },
    { key: 'inspiring', icon: '⭐', label: 'प्रेरक',  activeColor: '#6d28d9', activeBg: '#ede9fe' },
    { key: 'helpful',   icon: '✓',  label: 'उपयोगी', activeColor: '#065f46', activeBg: '#d1fae5' },
  ];

  return (
    <div style={{ background: '#fff' }}>
      {/* Category accent bar */}
      <div style={{ height: 3, background: cat.color }} />

      {/* Author row */}
      <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
        {post.author_photo && !imgErr
          ? <img src={post.author_photo} alt=""
              onError={() => setImgErr(true)}
              style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover',
                objectPosition: 'top', border: `2px solid ${cat.color}`, flexShrink: 0 }} />
          : <div style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              background: cat.bg, border: `2px solid ${cat.color}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 800, color: cat.color }}>
              {ini(post.author_name)}
            </div>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1c1e21', lineHeight: 1.2 }}>{post.author_name}</div>
            <LastSeenBadge ts={post.author_last_active} />
          </div>
          <div style={{ fontSize: 11, color: '#65676b', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
            <EnvironmentOutlined style={{ fontSize: 10 }} />
            {[post.author_village, post.author_district].filter(Boolean).join(', ') || 'ABRSVS'}
            <span>·</span>
            {timeAgo(post.created_at)}
          </div>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
          background: cat.bg, color: cat.color,
          display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0,
        }}>
          {cat.icon} {cat.label}
        </span>
        {/* Own post menu */}
        {isOwn && (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button onClick={() => setShowMenu(m => !m)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, color: '#9ca3af', fontSize: 18, lineHeight: 1 }}>
              ⋯
            </button>
            {showMenu && (
              <div style={{ position: 'absolute', right: 0, top: '100%', background: '#fff', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', zIndex: 100, minWidth: 130, overflow: 'hidden' }}>
                <button onClick={handleDelete} disabled={deleting}
                  style={{ width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <DeleteOutlined /> {deleting ? 'हटाया जा रहा…' : 'पोस्ट हटाएं'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Title + Content */}
      <div style={{ padding: '0 16px 10px' }}>
        {post.title && (
          <div style={{ fontSize: 15, fontWeight: 800, color: '#1c1e21', marginBottom: 5, lineHeight: 1.4 }}>
            {post.title}
          </div>
        )}
        <div style={{ fontSize: 14, color: '#3e4040', lineHeight: 1.75,
          ...(shouldClamp && !expanded ? {
            display: '-webkit-box', WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          } : {}),
        }}>
          {post.content}
        </div>
        {shouldClamp && (
          <button onClick={() => setExpanded(e => !e)}
            style={{ background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 700, color: '#65676b', padding: '2px 0', marginTop: 2 }}>
            {expanded ? 'कम देखें' : 'और देखें'}
          </button>
        )}
      </div>

      {/* Full-width image — no cropping */}
      {post.image_url && (
        <div onClick={() => onOpen(post)} style={{ cursor: 'zoom-in', background: '#000' }}>
          <img src={post.image_url} alt="post"
            onError={e => { e.target.parentElement.style.display = 'none'; }}
            style={{ width: '100%', display: 'block', height: 'auto', maxHeight: '70vh' }} />
        </div>
      )}

      {/* Tags */}
      {post.tags && (
        <div style={{ padding: '8px 16px 0', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {post.tags.split(',').slice(0, 5).map(tag => (
            <span key={tag} style={{ fontSize: 11, background: '#f0f2f5', color: '#65676b',
              borderRadius: 20, padding: '2px 9px' }}>
              #{tag.trim()}
            </span>
          ))}
        </div>
      )}

      {/* Counts row — views + reactions + comments */}
      {(post.reaction_count > 0 || post.comment_count > 0 || post.views > 0) && (
        <div style={{ padding: '8px 16px 4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {post.views > 0 && (
              <span style={{ fontSize: 12, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 3 }}>
                <EyeOutlined style={{ fontSize: 13 }} /> {post.views}
              </span>
            )}
            {post.reaction_count > 0 && (
              <span style={{ fontSize: 13, color: '#65676b', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 15 }}>👍</span> {post.reaction_count}
              </span>
            )}
          </div>
          {post.comment_count > 0 && (
            <button onClick={onToggleComment || (() => onOpen(post))}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, color: '#65676b', padding: 0 }}>
              {post.comment_count} टिप्पणियाँ
            </button>
          )}
        </div>
      )}

      {/* Divider */}
      <div style={{ height: 1, background: '#e4e6eb', margin: '4px 12px 0' }} />

      {/* Action buttons row — full width, Facebook style */}
      <div style={{ display: 'flex', padding: '2px 4px 4px' }}>
        {REACTIONS.map(r => (
          <button key={r.key} onClick={() => onReact(post.id, r.key)} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            background: post.my_reaction === r.key ? r.activeBg : 'none',
            border: 'none', cursor: 'pointer', padding: '8px 4px', borderRadius: 6,
            fontSize: 13, fontWeight: post.my_reaction === r.key ? 700 : 600,
            color: post.my_reaction === r.key ? r.activeColor : '#65676b',
            transition: 'background 0.15s',
          }}>
            <span style={{ fontSize: 15 }}>{r.icon}</span> {r.label}
          </button>
        ))}
        {/* Comment toggle button */}
        <button onClick={onToggleComment || (() => onOpen(post))} style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          background: isCommentExpanded ? '#f0f2f5' : 'none',
          border: 'none', cursor: 'pointer', padding: '8px 4px', borderRadius: 6,
          fontSize: 13, fontWeight: 600,
          color: isCommentExpanded ? '#7c3aed' : '#65676b',
          transition: 'background 0.15s',
        }}>
          <MessageOutlined style={{ fontSize: 14 }} /> टिप्पणी
        </button>
      </div>
    </div>
  );
}

export function PostDetailModal({ post: initialPost, onClose, myUserId, onDelete }) {
  const [post,         setPost]         = useState(initialPost);
  const [loading,      setLoading]      = useState(true);
  const [commentText,  setCommentText]  = useState('');
  const [submitting,   setSubmitting]   = useState(false);
  const [previewImg,   setPreviewImg]   = useState(null);
  const [myReaction,   setMyReaction]   = useState(initialPost?.my_reaction || null);
  const [reactCount,   setReactCount]   = useState(initialPost?.reaction_count || 0);
  const [deleting,     setDeleting]     = useState(false);
  const [replyTo,      setReplyTo]      = useState(null); // { id, name }
  const [commentLikes, setCommentLikes] = useState({}); // { commentId: { count, liked } }

  const REACTIONS = [
    { key: 'like',      icon: '👍', label: 'पसंद',   activeColor: '#9a3412', activeBg: '#fef3c7' },
    { key: 'inspiring', icon: '⭐', label: 'प्रेरक',  activeColor: '#6d28d9', activeBg: '#ede9fe' },
    { key: 'helpful',   icon: '✓',  label: 'उपयोगी', activeColor: '#065f46', activeBg: '#d1fae5' },
  ];

  const handleReact = async (reaction) => {
    try {
      const r = await axios.post(`${baseUrl}/member/posts/${post.id}/react`, { reaction }, { headers: authHeader() });
      if (r.data.status) {
        const removed = r.data.removed;
        setMyReaction(removed ? null : reaction);
        setReactCount(c => removed ? c - 1 : (myReaction ? c : c + 1));
      }
    } catch {}
  };

  const handleDeletePost = async () => {
    if (!window.confirm('क्या आप यह पोस्ट हटाना चाहते हैं?')) return;
    setDeleting(true);
    try {
      await axios.delete(`${baseUrl}/member/posts/${post.id}`, { headers: authHeader() });
      onDelete?.(post.id);
      onClose();
    } catch { notification.error({ message: 'पोस्ट हटाने में त्रुटि' }); setDeleting(false); }
  };

  useEffect(() => {
    if (!initialPost) return;
    setLoading(true);
    axios.get(`${baseUrl}/member/posts/${initialPost.id}`, { headers: authHeader() })
      .then(r => { if (r.data.status) setPost(r.data.result); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [initialPost?.id]); // eslint-disable-line

  const COMMENT_URL_RE = /https?:\/\/|www\.|\.com\b|\.net\b|\.org\b/i;

  const submitComment = async () => {
    if (!commentText.trim()) return;
    if (COMMENT_URL_RE.test(commentText)) {
      notification.error({ message: 'Links की अनुमति नहीं है।' }); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${baseUrl}/member/posts/${post.id}/comments`,
        { comment: commentText.trim() }, { headers: authHeader() });
      if (res.data.status) {
        setPost(p => ({ ...p, comments: [...(p.comments || []), res.data.comment] }));
        setCommentText('');
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Error' });
    } finally { setSubmitting(false); }
  };

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(`${baseUrl}/member/posts/${post.id}/comments/${commentId}`, { headers: authHeader() });
      setPost(p => ({ ...p, comments: p.comments.filter(c => c.id !== commentId) }));
    } catch {}
  };

  const likeComment = async (commentId) => {
    try {
      const r = await axios.post(`${baseUrl}/member/posts/${post.id}/comments/${commentId}/like`, {}, { headers: authHeader() });
      if (r.data.status) {
        setCommentLikes(prev => ({
          ...prev,
          [commentId]: { liked: !prev[commentId]?.liked, count: r.data.like_count ?? ((prev[commentId]?.count || 0) + (prev[commentId]?.liked ? -1 : 1)) }
        }));
      }
    } catch {}
  };

  const submitReply = async () => {
    if (!commentText.trim() || !replyTo) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${baseUrl}/member/posts/${post.id}/comments/${replyTo.id}/reply`,
        { comment: commentText.trim() }, { headers: authHeader() });
      if (res.data.status) {
        setPost(p => ({
          ...p,
          comments: p.comments.map(c => c.id === replyTo.id
            ? { ...c, replies: [...(c.replies || []), res.data.comment] }
            : c)
        }));
        setCommentText('');
        setReplyTo(null);
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Error' });
    } finally { setSubmitting(false); }
  };

  if (!initialPost) return null;
  const cat = CAT_META[post.category] || CAT_META.thought;
  const initials = n => (n || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', width: '100%', maxWidth: 560, borderRadius: '16px',
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
            background: cat.bg, color: cat.color }}>
            {cat.emoji} {cat.label}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {post.user_id === myUserId && (
              <button onClick={handleDeletePost} disabled={deleting}
                style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 700, color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <DeleteOutlined /> {deleting ? '…' : 'हटाएं'}
              </button>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>×</button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
          {loading ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : (
            <>
              {/* Author */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                {post.author_photo
                  ? <img src={post.author_photo} alt={post.author_name}
                      style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${M}` }} />
                  : <div style={{ width: 42, height: 42, borderRadius: '50%', background: M,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: G }}>
                      {initials(post.author_name)}
                    </div>
                }
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{post.author_name}</div>
                  {post.author_father && <div style={{ fontSize: 11, color: '#6b7280' }}>पिता: {post.author_father}</div>}
                  <div style={{ fontSize: 10, color: '#9ca3af' }}>
                    {[post.author_village, post.author_tahsil, post.author_district].filter(Boolean).join(', ')}
                    {' · '}{timeAgo(post.created_at)}
                  </div>
                </div>
              </div>

              <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 800, color: '#111827' }}>{post.title}</h3>

              {post.image_url && (
                <div onClick={() => setPreviewImg(post.image_url)}
                  style={{ background: '#111827', borderRadius: 10, overflow: 'hidden', marginBottom: 12, cursor: 'zoom-in' }}>
                  <img src={post.image_url} alt="post"
                    style={{ display: 'block', width: '100%', maxHeight: '55vh', objectFit: 'contain', margin: '0 auto' }} />
                </div>
              )}

              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.8, whiteSpace: 'pre-wrap', marginBottom: 12 }}>
                {post.content}
              </div>

              {post.tags && (
                <div style={{ marginBottom: 12 }}>
                  {post.tags.split(',').map(t => (
                    <span key={t} style={{ display: 'inline-block', background: '#f3f4f6',
                      borderRadius: 20, padding: '2px 9px', fontSize: 10, color: '#6b7280', margin: '0 4px 4px 0' }}>
                      #{t.trim()}
                    </span>
                  ))}
                </div>
              )}

              {/* Stats */}
              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>
                👀 {(post.views || 0) + 1} views · 👍 {reactCount} reactions
              </div>

              {/* React buttons */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderTop: '1px solid #f3f4f6', borderBottom: '1px solid #f3f4f6', padding: '8px 0' }}>
                {REACTIONS.map(r => (
                  <button key={r.key} onClick={() => handleReact(r.key)} style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    background: myReaction === r.key ? r.activeBg : '#f9fafb',
                    border: `1px solid ${myReaction === r.key ? r.activeColor + '40' : '#e5e7eb'}`,
                    borderRadius: 8, padding: '7px 4px', cursor: 'pointer',
                    fontSize: 12, fontWeight: myReaction === r.key ? 800 : 600,
                    color: myReaction === r.key ? r.activeColor : '#65676b',
                    transition: 'all 0.15s',
                  }}>
                    <span style={{ fontSize: 14 }}>{r.icon}</span> {r.label}
                  </button>
                ))}
              </div>

              {/* Comments */}
              <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>
                टिप्पणियाँ ({(post.comments || []).length})
              </div>
              {(post.comments || []).map(c => {
                const cl = commentLikes[c.id] || { liked: c.my_like || false, count: c.like_count || 0 };
                return (
                  <div key={c.id} style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                    {c.author_photo
                      ? <img src={c.author_photo} alt={c.author_name}
                          style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: '1.5px solid #e5e7eb', flexShrink: 0 }} />
                      : <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f3f4f6', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#6b7280' }}>
                          {initials(c.author_name)}
                        </div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ background: '#f9fafb', borderRadius: '0 10px 10px 10px', padding: '8px 10px', position: 'relative' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 2 }}>
                          {c.author_name}
                          <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>{c.author_village || ''}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#111827' }}>{c.comment}</div>
                        {cl.count > 0 && (
                          <div style={{ position: 'absolute', bottom: -8, right: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 20, padding: '1px 7px', fontSize: 10, display: 'flex', alignItems: 'center', gap: 3, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                            👍 <span style={{ fontWeight: 700, color: '#374151' }}>{cl.count}</span>
                          </div>
                        )}
                      </div>

                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: cl.count > 0 ? 14 : 4, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span>{timeAgo(c.created_at)}</span>
                        <button onClick={() => likeComment(c.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 10, fontWeight: 700, color: cl.liked ? '#9a3412' : '#6b7280' }}>
                          {cl.liked ? '👍 पसंद है' : '👍 पसंद'}
                        </button>
                        <button onClick={() => { setReplyTo({ id: c.id, name: c.author_name }); setCommentText(`@${c.author_name} `); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 10, fontWeight: 700, color: '#6b7280' }}>
                          ↩ जवाब देवें
                        </button>
                        {c.user_id === myUserId && (
                          <button onClick={() => deleteComment(c.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 10, color: '#ef4444' }}>
                            हटाएं
                          </button>
                        )}
                      </div>

                      {(c.replies || []).map(r => (
                        <div key={r.id} style={{ display: 'flex', gap: 6, marginTop: 8, paddingLeft: 8, borderLeft: '2px solid #e5e7eb' }}>
                          {r.author_photo
                            ? <img src={r.author_photo} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', border: '1px solid #e5e7eb', flexShrink: 0 }} />
                            : <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#e5e7eb', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#6b7280' }}>
                                {initials(r.author_name)}
                              </div>
                          }
                          <div style={{ background: '#f3f4f6', borderRadius: '0 8px 8px 8px', padding: '6px 8px', flex: 1 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#374151', marginBottom: 1 }}>{r.author_name}</div>
                            <div style={{ fontSize: 11, color: '#111827' }}>{r.comment}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Comment / Reply input */}
        <div style={{ borderTop: '1px solid #f3f4f6', flexShrink: 0 }}>
          {replyTo && (
            <div style={{ padding: '6px 14px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 10, color: '#6b7280' }}>↩ <b>{replyTo.name}</b> को जवाब</span>
              <button onClick={() => { setReplyTo(null); setCommentText(''); }}
                style={{ background: 'none', border: 'none', fontSize: 13, color: '#9ca3af', cursor: 'pointer' }}>×</button>
            </div>
          )}
          <div style={{ padding: '8px 14px', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <Input.TextArea
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder={replyTo ? `${replyTo.name} को जवाब देवें…` : 'अपनी टिप्पणी लिखें…'}
              autoSize={{ minRows: 1, maxRows: 3 }}
              style={{ flex: 1, borderRadius: 10, fontSize: 12 }}
              onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); replyTo ? submitReply() : submitComment(); } }}
            />
            <Button type="primary" onClick={replyTo ? submitReply : submitComment} loading={submitting}
              disabled={!commentText.trim()}
              style={{ background: M, borderColor: M, borderRadius: 10, flexShrink: 0 }}>
              भेजें
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function InlineCommentSection({ post, myUserId, myProfile }) {
  const [comments,   setComments]   = useState(post.comments || []);
  const [loaded,     setLoaded]     = useState(!!(post.comments));
  const [loading,    setLoading]    = useState(false);
  const [text,       setText]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyTo,    setReplyTo]    = useState(null); // { id, name }
  const [replyText,  setReplyText]  = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const inputRef = useRef(null);
  const replyInputRef = useRef(null);
  const ini = n => (n || '?').trim().split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
  const COMMENT_URL_RE = /https?:\/\/|www\.|\.com\b|\.net\b|\.org\b/i;

  const load = async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/member/posts/${post.id}`, { headers: authHeader() });
      if (r.data.status) setComments(r.data.result.comments || []);
      setLoaded(true);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []); // eslint-disable-line

  const submit = async () => {
    if (!text.trim()) return;
    if (COMMENT_URL_RE.test(text)) { notification.error({ message: 'Links की अनुमति नहीं है।' }); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${baseUrl}/member/posts/${post.id}/comments`, { comment: text.trim() }, { headers: authHeader() });
      if (r.data.status) { setComments(p => [...p, r.data.comment]); setText(''); }
    } catch (e) { notification.error({ message: e.response?.data?.message || 'Error' }); }
    finally { setSubmitting(false); }
  };

  const del = async (cId) => {
    try {
      await axios.delete(`${baseUrl}/member/posts/${post.id}/comments/${cId}`, { headers: authHeader() });
      setComments(p => p.filter(c => c.id !== cId));
    } catch {}
  };

  const likeComment = async (cId) => {
    try {
      const r = await axios.post(`${baseUrl}/member/posts/${post.id}/comments/${cId}/like`, {}, { headers: authHeader() });
      if (r.data.status) {
        setComments(prev => prev.map(c => c.id === cId
          ? { ...c, liked: r.data.liked, like_count: r.data.like_count }
          : c));
      }
    } catch {}
  };

  const submitReply = async () => {
    if (!replyText.trim() || !replyTo) return;
    setReplySubmitting(true);
    try {
      const r = await axios.post(`${baseUrl}/member/posts/${post.id}/comments/${replyTo.id}/reply`, { comment: replyText.trim() }, { headers: authHeader() });
      if (r.data.status) {
        setComments(prev => [...prev, r.data.comment]);
        setReplyText(''); setReplyTo(null);
      }
    } catch (e) { notification.error({ message: e.response?.data?.message || 'Error' }); }
    finally { setReplySubmitting(false); }
  };

  const myPhotoName = myProfile?.name_hn || myProfile?.name_eng;
  const myPhoto = myProfile?.profile_img;
  const topLevel = comments.filter(c => !c.parent_comment_id);
  const repliesFor = (cId) => comments.filter(c => Number(c.parent_comment_id) === cId);

  return (
    <div style={{ borderTop: '1px solid #e4e6eb', padding: '10px 12px 14px', background: '#fff' }}>
      {loading && <div style={{ textAlign: 'center', padding: 8 }}><Spin size="small" /></div>}

      {topLevel.map(c => (
        <div key={c.id} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            {c.author_photo
              ? <img src={c.author_photo} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', flexShrink: 0 }} />
              : <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e4e6eb', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#65676b' }}>{ini(c.author_name)}</div>
            }
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ background: '#f0f2f5', borderRadius: '0 18px 18px 18px', padding: '8px 12px', display: 'inline-block', maxWidth: '100%', position: 'relative' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1c1e21', marginBottom: 1 }}>
                  {c.author_name}
                  {c.author_village && <span style={{ fontSize: 11, color: '#65676b', fontWeight: 400, marginLeft: 5 }}>{c.author_village}</span>}
                </div>
                <div style={{ fontSize: 13, color: '#1c1e21', lineHeight: 1.5 }}>{c.comment}</div>
                {c.like_count > 0 && (
                  <div style={{ position: 'absolute', bottom: -8, right: 6, background: '#fff', border: '1px solid #e4e6eb', borderRadius: 20, padding: '1px 6px', fontSize: 10, color: '#65676b', display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    ❤️ {c.like_count}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#65676b', marginTop: 6, paddingLeft: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                {timeAgo(c.created_at)}
                <button onClick={() => likeComment(c.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11, fontWeight: 700, color: c.liked ? '#ef4444' : '#65676b' }}>
                  {c.liked ? '❤️ पसंद' : '🤍 पसंद'}
                </button>
                <button onClick={() => { setReplyTo({ id: c.id, name: c.author_name }); setTimeout(() => replyInputRef.current?.focus(), 80); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11, fontWeight: 700, color: M }}>
                  ↩ Reply
                </button>
                {c.user_id === myUserId && (
                  <button onClick={() => del(c.id)} style={{ background: 'none', border: 'none', color: '#65676b', fontSize: 11, cursor: 'pointer', padding: 0, fontWeight: 600 }}>हटाएं</button>
                )}
              </div>
            </div>
          </div>

          {repliesFor(c.id).map(r => (
            <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, marginLeft: 40 }}>
              {r.author_photo
                ? <img src={r.author_photo} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', flexShrink: 0 }} />
                : <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#e4e6eb', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#65676b' }}>{ini(r.author_name)}</div>
              }
              <div style={{ flex: 1 }}>
                <div style={{ background: '#f0f2f5', borderRadius: '0 14px 14px 14px', padding: '6px 10px', display: 'inline-block', maxWidth: '100%' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#1c1e21', marginBottom: 1 }}>{r.author_name}</div>
                  <div style={{ fontSize: 12, color: '#1c1e21', lineHeight: 1.5 }}>{r.comment}</div>
                </div>
                <div style={{ fontSize: 10, color: '#65676b', marginTop: 3, paddingLeft: 10, display: 'flex', gap: 10 }}>
                  {timeAgo(r.created_at)}
                  <button onClick={() => likeComment(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 10, fontWeight: 700, color: r.liked ? '#ef4444' : '#65676b' }}>
                    {r.liked ? '❤️' : '🤍'} {r.like_count > 0 ? r.like_count : ''}
                  </button>
                  {r.user_id === myUserId && (
                    <button onClick={() => del(r.id)} style={{ background: 'none', border: 'none', color: '#65676b', fontSize: 10, cursor: 'pointer', padding: 0 }}>हटाएं</button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {replyTo?.id === c.id && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, marginLeft: 40 }}>
              <div style={{ flex: 1, background: '#f0f2f5', borderRadius: 20, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: M, fontWeight: 700, flexShrink: 0 }}>↩ {replyTo.name} को:</span>
                <input ref={replyInputRef} value={replyText} onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submitReply(); if (e.key === 'Escape') { setReplyTo(null); setReplyText(''); } }}
                  placeholder="Reply लिखें…" style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: '#1c1e21', fontFamily: 'inherit' }} />
              </div>
              <button onClick={submitReply} disabled={!replyText.trim() || replySubmitting}
                style={{ background: 'none', border: 'none', cursor: replyText.trim() ? 'pointer' : 'default', color: replyText.trim() ? M : '#bcc0c4', fontSize: 16 }}>
                {replySubmitting ? <Spin size="small" /> : <SendOutlined />}
              </button>
              <button onClick={() => { setReplyTo(null); setReplyText(''); }}
                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 14 }}>×</button>
            </div>
          )}
        </div>
      ))}

      {/* Comment input — Facebook pill style */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: comments.length > 0 ? 8 : 4 }}>
        {myPhoto
          ? <img src={myPhoto} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', flexShrink: 0 }} />
          : <div style={{ width: 32, height: 32, borderRadius: '50%', background: M, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 800, color: '#fff' }}>
              {ini(myPhotoName)}
            </div>
        }
        <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center',
          background: '#f0f2f5', borderRadius: 24, padding: '0 12px', minHeight: 36 }}>
          <textarea
            ref={inputRef}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="टिप्पणी लिखें…"
            rows={1}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
            style={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              fontSize: 13, color: '#1c1e21', resize: 'none', padding: '8px 0',
              fontFamily: 'inherit', lineHeight: 1.4,
            }}
            onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'; }}
          />
          <button onClick={submit} disabled={!text.trim() || submitting}
            style={{
              background: 'none', border: 'none', cursor: text.trim() ? 'pointer' : 'default',
              color: text.trim() ? M : '#bcc0c4', padding: '4px 0 4px 8px', flexShrink: 0,
              fontSize: 18, display: 'flex', alignItems: 'center', transition: 'color 0.15s',
            }}>
            {submitting ? <Spin size="small" /> : <SendOutlined />}
          </button>
        </div>
      </div>
    </div>
  );
}

export function WritePostModal({ open, onClose, onPosted, isActiveUser = true }) {
  const [category,    setCategory]    = useState('thought');
  const [title,       setTitle]       = useState('');
  const [content,     setContent]     = useState('');
  const [tags,        setTags]        = useState('');
  const [imageFile,   setImageFile]   = useState(null);
  const [imagePreview,setImagePreview]= useState(null);
  const [submitting,  setSubmitting]  = useState(false);

  const reset = () => { setCategory('thought'); setTitle(''); setContent(''); setTags(''); setImageFile(null); setImagePreview(null); };

  const handleImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const URL_RE = /https?:\/\/|www\.|\.com\b|\.net\b|\.org\b|\.in\b/i;

  const submit = async () => {
    if (!title.trim())   { notification.warning({ message: 'शीर्षक आवश्यक है।' }); return; }
    if (!content.trim()) { notification.warning({ message: 'विवरण आवश्यक है।' }); return; }
    if (URL_RE.test(title) || URL_RE.test(content) || URL_RE.test(tags)) {
      notification.error({ message: 'Links की अनुमति नहीं है। URL/website addresses हटाएं।' }); return;
    }
    setSubmitting(true);
    try {
      let image_url = null;
      if (imageFile) {
        const fd = new FormData();
        fd.append('image', imageFile);
        const imgRes = await axios.post(`${baseUrl}/member/posts/upload-image`, fd, {
          headers: authHeader(),
        });
        if (imgRes.data.status) image_url = imgRes.data.image_url;
      }

      const res = await axios.post(`${baseUrl}/member/posts`, {
        category,
        title:        title.trim(),
        content:      content.trim(),
        tags:         tags.trim() || undefined,
        image_url,
        needs_review: !isActiveUser,
      }, { headers: authHeader() });

      if (res.data.status) {
        if (!isActiveUser) {
          notification.info({
            message: '📋 पोस्ट समीक्षा के लिए भेजी गई',
            description: 'आपका खाता निष्क्रिय है। Admin की समीक्षा के बाद पोस्ट प्रकाशित होगी।',
            duration: 6, placement: 'topRight',
          });
        } else {
          notification.success({ message: '✅ पोस्ट प्रकाशित हो गई!' });
        }
        reset(); onClose(); onPosted?.();
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Submit failed' });
    } finally { setSubmitting(false); }
  };

  if (!open) return null;
  const cat = CAT_META[category] || CAT_META.thought;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }} onClick={e => { if (e.target === e.currentTarget) { reset(); onClose(); } }}>
      <div style={{
        background: '#fff', width: '100%', maxWidth: 560, borderRadius: 20,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          background: `linear-gradient(135deg, ${M}, #b71c1c)`, borderRadius: '20px 20px 0 0',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>✍️ नई पोस्ट लिखें</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>
              {isActiveUser ? 'समाज मंच — Community Board' : '⚠️ निष्क्रिय खाता — पोस्ट Admin समीक्षा के बाद दिखेगी'}
            </div>
          </div>
          <button onClick={() => { reset(); onClose(); }}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
              borderRadius: '50%', width: 32, height: 32, cursor: 'pointer',
              fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Form */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
          {/* Category pills */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              श्रेणी चुनें
            </div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {CATS.map(c => (
                <button key={c.key} onClick={() => setCategory(c.key)} style={{
                  background: category === c.key ? c.bg : '#f9fafb',
                  border: `2px solid ${category === c.key ? c.color : '#e5e7eb'}`,
                  borderRadius: 22, padding: '5px 14px', fontSize: 12, fontWeight: 700,
                  color: category === c.key ? c.color : '#9ca3af', cursor: 'pointer',
                  transition: 'all 0.15s',
                  boxShadow: category === c.key ? `0 2px 8px ${c.color}30` : 'none',
                }}>
                  {c.emoji} {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              शीर्षक *
            </div>
            <Input
              value={title} onChange={e => setTitle(e.target.value)}
              placeholder="पोस्ट का शीर्षक लिखें…"
              style={{ borderRadius: 10, fontSize: 14, fontWeight: 600, padding: '10px 12px',
                border: `1.5px solid ${title ? cat.color : '#e5e7eb'}` }}
              maxLength={255}
            />
            <div style={{ textAlign: 'right', fontSize: 10, color: '#9ca3af', marginTop: 3 }}>{title.length}/255</div>
          </div>

          {/* Content */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              विवरण *
            </div>
            <Input.TextArea
              value={content} onChange={e => setContent(e.target.value)}
              placeholder={`${cat.emoji} अपने विचार, समाचार, या जानकारी यहाँ लिखें…\n\nहिंदी या English दोनों में लिख सकते हैं।`}
              autoSize={{ minRows: 4, maxRows: 10 }}
              style={{ borderRadius: 10, fontSize: 13, lineHeight: 1.7, padding: '10px 12px',
                border: `1.5px solid ${content ? cat.color : '#e5e7eb'}` }}
              maxLength={3000}
            />
            <div style={{ textAlign: 'right', fontSize: 10, color: '#9ca3af', marginTop: 3 }}>{content.length}/3000</div>
          </div>

          {/* Tags */}
          <Input
            value={tags} onChange={e => setTags(e.target.value)}
            prefix={<span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 700 }}>#</span>}
            placeholder="Tags: नौकरी, Pali, SN — comma से अलग करें (optional)"
            style={{ borderRadius: 10, fontSize: 12, marginBottom: 12, border: '1.5px solid #e5e7eb' }}
            maxLength={200}
          />

          {/* Image upload */}
          <div style={{ marginBottom: 12 }}>
            {!imagePreview ? (
              <label style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: '#fafafa', border: '2px dashed #d1d5db', borderRadius: 12,
                padding: '16px', cursor: 'pointer', fontSize: 13, color: '#9ca3af', fontWeight: 600,
              }}>
                <CameraOutlined style={{ fontSize: 20, color: '#d1d5db' }} />
                फोटो जोड़ें (optional)
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImage} style={{ display: 'none' }} />
              </label>
            ) : (
              <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden' }}>
                <img src={imagePreview} alt="preview"
                  style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }} />
                <button onClick={() => { setImageFile(null); setImagePreview(null); }}
                  style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)',
                    border: 'none', borderRadius: '50%', color: '#fff', width: 28, height: 28,
                    cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  ×
                </button>
              </div>
            )}
          </div>

          <div style={{ fontSize: 11, color: '#9ca3af', background: '#f9fafb', borderRadius: 8, padding: '8px 12px',
            display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>ℹ️</span> आपकी पोस्ट तुरंत प्रकाशित होगी। Admin अनुचित पोस्ट हटा सकता है।
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #f0f0f0', flexShrink: 0,
          display: 'flex', gap: 10 }}>
          <Button onClick={() => { reset(); onClose(); }}
            style={{ borderRadius: 10, height: 44, flex: 1, fontWeight: 600 }}>
            रद्द करें
          </Button>
          <Button type="primary" loading={submitting} onClick={submit}
            disabled={!title.trim() || !content.trim()}
            style={{ background: M, borderColor: M, borderRadius: 10, height: 44, flex: 2,
              fontSize: 14, fontWeight: 800,
              boxShadow: !title.trim() || !content.trim() ? 'none' : `0 4px 15px ${M}50` }}>
            {submitting ? 'प्रकाशित हो रही है…' : '🚀 पोस्ट प्रकाशित करें'}
          </Button>
        </div>
      </div>
    </div>
  );
}
